import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { AiProposal, aiConfirmProposal, aiRejectProposal } from '../../../service/ai/ai-service';
import './AiProposalCard.css';

interface Props {
    businessId: number;
    proposal: AiProposal;
}

type CardState = 'pending' | 'working' | 'executed' | 'failed' | 'rejected';

/**
 * Confirmation card of one AI action proposal (propose → confirm → execute).
 * Every row comes from the SERVER-side proposal — never from model text — and
 * the backend re-validates ownership + expiry + the EDIT permission on confirm.
 */
export default function AiProposalCard({ businessId, proposal }: Props) {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const [state, setState] = useState<CardState>('pending');
    const [resultMessage, setResultMessage] = useState('');

    const title = t(`ai.proposal.action.${proposal.action}`, { defaultValue: proposal.title });

    const confirm = async () => {
        setState('working');
        try {
            const token = await getAccessTokenSilently();
            const result = await aiConfirmProposal(businessId, proposal.id, token);
            setResultMessage(result.message);
            setState(result.ok ? 'executed' : 'failed');
        } catch (e) {
            setResultMessage(e instanceof Error && e.message && !e.message.startsWith('ai-service')
                ? e.message : t('ai.proposal.errorGeneric'));
            setState('failed');
        }
    };

    const reject = async () => {
        setState('working');
        try {
            const token = await getAccessTokenSilently();
            await aiRejectProposal(businessId, proposal.id, token);
        } catch {
            // Rejecting is best-effort — the proposal expires server-side anyway.
        }
        setState('rejected');
    };

    return (
        <div className={`ai-proposal ai-proposal--${state}`} data-cy="ai-proposal">
            <div className="ai-proposal__title">{title}</div>
            <div className="ai-proposal__details">
                {proposal.details.map((d) => (
                    <React.Fragment key={d.label}>
                        <span className="ai-proposal__label">{d.label}</span>
                        <span className="ai-proposal__value">{d.value}</span>
                    </React.Fragment>
                ))}
            </div>

            {state === 'pending' && (
                <div className="ai-proposal__actions">
                    <button type="button" className="ai-proposal__btn ai-proposal__btn--confirm"
                            onClick={() => void confirm()} data-cy="ai-proposal-confirm">
                        <FontAwesomeIcon icon={faCheck} /> {t('ai.proposal.confirm')}
                    </button>
                    <button type="button" className="ai-proposal__btn ai-proposal__btn--reject"
                            onClick={() => void reject()} data-cy="ai-proposal-reject">
                        <FontAwesomeIcon icon={faXmark} /> {t('ai.proposal.reject')}
                    </button>
                </div>
            )}
            {state === 'working' && (
                <div className="ai-proposal__result ai-proposal__result--working">
                    {t('ai.proposal.working')}
                </div>
            )}
            {state === 'executed' && (
                <div className="ai-proposal__result ai-proposal__result--ok" data-cy="ai-proposal-ok">
                    <FontAwesomeIcon icon={faCircleCheck} /> {resultMessage}
                </div>
            )}
            {state === 'failed' && (
                <div className="ai-proposal__result ai-proposal__result--error" data-cy="ai-proposal-error">
                    <FontAwesomeIcon icon={faCircleXmark} /> {resultMessage}
                </div>
            )}
            {state === 'rejected' && (
                <div className="ai-proposal__result ai-proposal__result--rejected" data-cy="ai-proposal-rejected">
                    {t('ai.proposal.rejected')}
                </div>
            )}
        </div>
    );
}
